import {
  Button,
  ConfirmDialog,
  HookedForm,
  Loading,
  PaymentCardForm,
  PaymentMethodForm,
  RadioField,
  Tooltip,
} from '@app/components';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  RadioGroup,
} from '@headlessui/react';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { UserCardInterface } from '@app/models/user';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCards,
  updatePaymentMethod,
  deletePaymentMethod,
} from '@app/services/user';
import { toast } from 'react-toastify';
import NoCardIllustration from '@app/assets/illustrations/no-card.svg?react';
import AddCardIcon from '@app/assets/icons/add-card.svg?react';
import AddIcon from '@app/assets/icons/add.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import LeftArrowIcon from '@app/assets/icons/arrow-left-light-gray.svg?react';
import InfoIcon from '@app/assets/icons/info-gray.svg?react';
import HorizontalDotsIcon from '@app/assets/icons/dot-horizontal.svg?react';
import TrashRedIcon from '@app/assets/icons/trash-red.svg?react';
import EditIcon from '@app/assets/icons/edit-03.svg?react';
import VisaLogo from '@app/assets/images/visa.svg';
import MastercardLogo from '@app/assets/images/mastercard.svg';
import AmexLogo from '@app/assets/images/amex.svg';
import { useAddPaymentMethodContext } from '../Settings';
import { usePermissions } from '@app/contexts/PermissionContext';

type DialogStep = 'closed' | 'add-card' | 'edit-card';

const CardBrandBadge = ({ brand }: { brand: string }) => {
  const lower = brand.toLowerCase();

  if (lower === 'visa') {
    return <img src={VisaLogo} alt="Visa" className="h-7 w-auto" />;
  }
  if (lower === 'mastercard') {
    return <img src={MastercardLogo} alt="Mastercard" className="h-7 w-auto" />;
  }
  if (lower === 'amex' || lower === 'american express') {
    return <img src={AmexLogo} alt="Amex" className="h-7 w-auto" />;
  }

  return (
    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold uppercase bg-base-davyGray text-white">
      {brand}
    </span>
  );
};

export const PaymentMethods = () => {
  const queryClient = useQueryClient();
  const [dialogStep, setDialogStep] = useState<DialogStep>('closed');
  const [editingCard, setEditingCard] = useState<UserCardInterface | null>(null);
  const [cardToDelete, setCardToDelete] = useState<UserCardInterface | null>(
    null
  );
  const { showAddPaymentMethod, setShowAddPaymentMethod } =
    useAddPaymentMethodContext();
  const { can } = usePermissions();
  const canEdit = can('editPaymentMethods');

  const {
    data: cardsResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['user-cards'],
    queryFn: getCards,
  });

  const cards: UserCardInterface[] = cardsResponse?.data?.cards ?? [];
  const defaultCardId = cards.find((c) => c.isDefault)?.id;

  useEffect(() => {
    if (showAddPaymentMethod) {
      setDialogStep('add-card');
      setEditingCard(null);
      setShowAddPaymentMethod(false);
    }
  }, [showAddPaymentMethod, setShowAddPaymentMethod]);

  // Set default mutation with optimistic update
  const setDefaultMutation = useMutation({
    mutationFn: (paymentMethodId: string) =>
      updatePaymentMethod({ paymentMethodId, isDefault: true }),
    onMutate: async (paymentMethodId) => {
      await queryClient.cancelQueries({ queryKey: ['user-cards'] });
      const previous = queryClient.getQueryData(['user-cards']);
      queryClient.setQueryData(['user-cards'], (old: typeof cardsResponse) => {
        if (!old?.data?.cards) return old;
        return {
          ...old,
          data: {
            ...old.data,
            cards: old.data.cards.map((card: UserCardInterface) => ({
              ...card,
              isDefault: card.id === paymentMethodId,
            })),
          },
        };
      });
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['user-cards'], context.previous);
      }
      toast.error('Failed to set default payment method');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cards'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (paymentMethodId: string) =>
      deletePaymentMethod(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cards'] });
      toast.success('Payment method removed');
      setCardToDelete(null);
    },
    onError: () => {
      toast.error('Failed to remove payment method');
    },
  });

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: (data: {
      paymentMethodId: string;
      name: string;
      exp_month: number;
      exp_year: number;
    }) => updatePaymentMethod(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-cards'] });
      toast.success('Payment method updated');
      closeDialog();
    },
    onError: () => {
      toast.error('Failed to update payment method');
    },
  });

  const handlePaymentMethodChange = (paymentMethodId: string) => {
    setDefaultMutation.mutate(paymentMethodId);
  };

  const openAddFlow = () => {
    setEditingCard(null);
    setDialogStep('add-card');
  };

  const openEditFlow = (card: UserCardInterface) => {
    setEditingCard(card);
    setDialogStep('edit-card');
  };

  const closeDialog = () => {
    setDialogStep('closed');
    setEditingCard(null);
  };

  const handleAddSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['user-cards'] });
    toast.success('Payment method added');
    closeDialog();
  };

  const handleEditSave = (data: {
    name: string;
    exp_month: number;
    exp_year: number;
  }) => {
    if (!editingCard) return;
    editMutation.mutate({ paymentMethodId: editingCard.id, ...data });
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="container mx-auto p-4 flex items-center justify-center mt-20"
      >
        <Loading />
      </motion.div>
    );
  }

  if (isError) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="container mx-auto p-4 flex items-center justify-center flex-col mt-20 space-y-4"
      >
        <p className="text-sm text-gray-500">Failed to load payment methods</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto p-4 space-y-6"
    >
      {cards.length > 0 && (
        <HookedForm mode="onChange">
          <RadioGroup
            name="defaultCard"
            value={defaultCardId}
            onChange={handlePaymentMethodChange}
            className="flex flex-wrap gap-6"
          >
            {cards.map((card) => (
              <div
                className="bg-white py-4 px-6 rounded-lg relative min-w-[320px] max-w-[400px] flex-1"
                key={card.id}
              >
                <div className="flex justify-between items-start pb-4 mb-4 border-b border-base-ghostWhite">
                  <CardBrandBadge brand={card.brand} />
                  {canEdit && (
                    <Menu>
                      <MenuButton className="flex items-center justify-center w-8 h-8 rounded-[4px] bg-base-ghostWhite hover:bg-base-antiFlash transition-colors">
                        <HorizontalDotsIcon />
                      </MenuButton>
                      <MenuItems
                        transition
                        anchor="left start"
                        className="p-2 shadow-lg mt-2 min-w-[220px] transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 rounded-lg border file:border-base-antiFlash"
                        style={{ backgroundColor: '#F6F7FA' }}
                      >
                        <MenuItem>
                          <button
                            className="flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full"
                            onClick={() => openEditFlow(card)}
                          >
                            <EditIcon />
                            Edit information
                          </button>
                        </MenuItem>
                        <MenuItem>
                          <button
                            className="flex items-center gap-2 p-2 rounded-lg data-[focus]:bg-brand-lightBlue4 text-xs w-full"
                            onClick={() => setCardToDelete(card)}
                          >
                            <TrashRedIcon className="[&_path]:stroke-system-error" />
                            Remove method
                          </button>
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  )}
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-base-coolGray text-xs">
                    DEBIT CARD ENDING IN
                  </span>
                  <span className="text-sm font-medium">
                    &bull;&bull;&bull;&bull; {card.last4}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-8">
                  <span className="text-base-coolGray text-xs">
                    CARDHOLDER NAME
                  </span>
                  <span className="text-sm font-medium">
                    {card.name || '\u2014'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <RadioField
                    label="Set this payment method as active now"
                    name="defaultCard"
                    value={card.id}
                  />
                  <Tooltip
                    content={
                      <div className="text-center">
                        <div>This payment method will be utilized</div>
                        <div>for the selected plan.</div>
                      </div>
                    }
                    direction="up"
                  >
                    <InfoIcon className="cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
            ))}
            {canEdit && (
              <div
                className="bg-white/80 p-4 rounded-lg border-2 border-dashed border-base-coolGray/40 flex items-center justify-center cursor-pointer min-w-[320px] max-w-[400px] flex-1"
                onClick={openAddFlow}
              >
                <AddIcon className="[&_path]:stroke-brand-baseBlue mr-2" />
                <span className="text-brand-baseBlue font-bold">
                  Add payment method
                </span>
              </div>
            )}
          </RadioGroup>
        </HookedForm>
      )}

      {cards.length === 0 && (
        <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
          <NoCardIllustration />
          <p className="font-bold text-gray-800">No payment methods</p>
          <p className="text-sm text-gray-500 text-center">
            Add the payment method that will be used to pay for the plan
          </p>
          {canEdit && (
            <Button
              leftNode={<AddCardIcon />}
              onClick={openAddFlow}
              className="font-medium bg-blue-600 text-white px-6 py-3 rounded-lg"
            >
              Add payment method
            </Button>
          )}
        </div>
      )}

      {/* Add / Edit card dialog */}
      <Dialog
        transition
        open={dialogStep !== 'closed'}
        onClose={closeDialog}
        className="fixed inset-0 flex w-screen items-center justify-center p-4 transition duration-300 ease-out data-[closed]:opacity-0 z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/15" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="border bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b border-b-base-ghostWhite p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="flex items-center p-1 rounded-lg hover:bg-base-ghostWhite"
                  onClick={closeDialog}
                >
                  <LeftArrowIcon width={20} height={20} />
                </button>
                <h5 className="font-bold text-lg leading-tight">
                  {dialogStep === 'edit-card'
                    ? 'Edit card'
                    : 'Add a credit or debit card'}
                </h5>
              </div>
              <button type="button" onClick={closeDialog}>
                <CloseIcon />
              </button>
            </div>
            <div className="px-6 pb-6 m-0">
              {dialogStep === 'add-card' && (
                <PaymentMethodForm
                  cancelAction={closeDialog}
                  onSuccess={handleAddSuccess}
                />
              )}
              {dialogStep === 'edit-card' && editingCard && (
                <PaymentCardForm
                  editCard={editingCard}
                  cancelAction={closeDialog}
                  onSubmit={handleEditSave}
                  isLoading={editMutation.isPending}
                />
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={cardToDelete !== null}
        onClose={() => setCardToDelete(null)}
        onConfirm={() => {
          if (cardToDelete) deleteMutation.mutate(cardToDelete.id);
        }}
        title="Remove payment method"
        message={`Are you sure you want to remove the card ending in ${cardToDelete?.last4 ?? ''}? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </motion.div>
  );
};
